import { useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'

export default function Modal({ open, onClose, title, description, children, size='max-w-lg' }) {
  const [isRendered, setIsRendered] = useState(open)
  const [isClosing, setIsClosing] = useState(false)
  const closeTimerRef = useRef(null)
  const panelRef = useRef(null)
  const onCloseRef = useRef(onClose)
  const titleId = useId()
  const descriptionId = useId()

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (open) {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
      setIsRendered(true)
      setIsClosing(false)
    } else if (isRendered) {
      setIsClosing(true)
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
      closeTimerRef.current = setTimeout(() => {
        setIsRendered(false)
        setIsClosing(false)
      }, 180)
    }
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
    }
  }, [open, isRendered])

  useEffect(() => {
    if (!isRendered || isClosing) return undefined
    const previous = document.activeElement
    const panel = panelRef.current
    const focusable = () => [...(panel?.querySelectorAll('button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),a[href],[tabindex]:not([tabindex="-1"])') || [])]
    queueMicrotask(() => focusable()[0]?.focus())
    const onKeyDown = event => {
      if (event.key === 'Escape') { event.preventDefault(); onCloseRef.current?.(); return }
      if (event.key !== 'Tab') return
      const items = focusable()
      if (!items.length) return
      const first = items[0]; const last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => { document.removeEventListener('keydown', onKeyDown); previous?.focus?.() }
  }, [isRendered, isClosing])

  if (!isRendered) return null

  return createPortal(
    <div className={`modal-shell fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 ${isClosing ? 'pointer-events-none' : ''}`}>
      <div
        className={`modal-backdrop absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-md transition-opacity ${isClosing ? 'modal-backdrop-exit' : ''}`}
        aria-hidden="true"
        onClick={() => onCloseRef.current?.()}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        className={`modal-panel ${isClosing ? 'modal-panel-exit' : 'modal-panel-spring'} relative rounded-2xl md:rounded-3xl w-full ${size} mx-auto max-h-[90vh] shadow-apple-dark-card border border-[var(--color-border-subtle)] overflow-hidden transition-[border-color,box-shadow]`}
        style={{ background: 'var(--color-surface-1)' }}
      >
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-[var(--color-border-subtle)]">
          <div className="min-w-0 pr-3">
            <h2 id={titleId} className="font-semibold text-sm sm:text-base text-[var(--color-text-primary)] tracking-tight truncate">
              {title}
            </h2>
            {description ? (
              <p id={descriptionId} className="modal-description text-xs text-[var(--color-text-muted)] mt-0.5">
                {description}
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="modal-close squircle-btn p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-2)] active:scale-[0.97] transition-[transform,background-color,color] duration-150 flex-shrink-0 cursor-pointer"
            aria-label="Fechar diálogo"
            onClick={() => onCloseRef.current?.()}
          >
            <X size={17} aria-hidden="true" />
          </button>
        </div>
        <div className="modal-body px-5 sm:px-6 py-4 sm:py-5 overflow-y-auto max-h-[calc(90vh-80px)]">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  )
}
